import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Map, Heart, Menu, X, FolderOpen, Images } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navItems = [
  { path: "/map", label: "旅行地图", icon: Map },
  { path: "/timeline", label: "时光相册", icon: Images },
  { path: "/photos", label: "照片管理", icon: FolderOpen },
];

export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isHome = location.pathname === "/";
  const showBg = scrolled || !isHome;

  return (
    <>
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          backgroundColor: showBg ? "oklch(0.97 0.015 70 / 0.85)" : "transparent",
          backdropFilter: showBg ? "blur(20px) saturate(1.4)" : "none",
          WebkitBackdropFilter: showBg ? "blur(20px) saturate(1.4)" : "none",
          boxShadow: showBg
            ? "0 1px 3px oklch(0.55 0.08 55 / 0.06), 0 8px 24px oklch(0.55 0.08 55 / 0.04)"
            : "none",
          borderBottom: showBg ? "1px solid oklch(0.90 0.025 65 / 0.4)" : "1px solid transparent",
        }}
      >
        <div
          className="container mx-auto flex items-center justify-between"
          style={{ padding: "var(--spacing-sm) var(--spacing-lg)", height: 64 }}
        >
          <Link to="/" className="flex items-center group" style={{ gap: "var(--spacing-sm)" }}>
            <motion.div
              whileHover={{ scale: 1.15, rotate: 5 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <Heart
                className="w-5 h-5 transition-colors"
                style={{ color: "oklch(0.62 0.18 35)" }}
                fill={showBg ? "oklch(0.62 0.18 35 / 0.2)" : "oklch(0.88 0.12 55 / 0.3)"}
              />
            </motion.div>
            <span
              className="font-semibold transition-colors duration-300"
              style={{
                fontFamily: "var(--font-handwritten)",
                fontSize: "var(--font-size-title)",
                color: showBg ? "var(--foreground)" : "oklch(0.98 0.01 70)",
                textShadow: showBg ? "none" : "0 1px 12px oklch(0.25 0.06 45 / 0.4)",
              }}
            >
              我们的旅行足迹
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center" style={{ gap: "var(--spacing-xs)" }}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className="relative flex items-center rounded-xl transition-all duration-300"
                  style={{
                    gap: "var(--spacing-xs)",
                    padding: "var(--spacing-xs) var(--spacing-md)",
                    fontSize: "var(--font-size-label)",
                    color: active
                      ? "oklch(0.62 0.18 35)"
                      : showBg
                        ? "var(--muted-foreground)"
                        : "oklch(0.95 0.02 65)",
                    fontWeight: active ? 600 : 500,
                    textShadow: showBg ? "none" : "0 1px 4px oklch(0.25 0.06 45 / 0.2)",
                    backgroundColor: active
                      ? "oklch(0.62 0.18 35 / 0.08)"
                      : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) {
                      e.currentTarget.style.backgroundColor = showBg
                        ? "oklch(0.62 0.18 35 / 0.06)"
                        : "oklch(1 0 0 / 0.1)";
                      e.currentTarget.style.color = showBg
                        ? "oklch(0.55 0.16 35)"
                        : "oklch(1 0 0)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = showBg
                        ? "var(--muted-foreground)"
                        : "oklch(0.95 0.02 65)";
                    }
                  }}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                  {active && (
                    <motion.div
                      layoutId="nav-pill"
                      className="absolute inset-0 rounded-xl -z-10"
                      style={{
                        backgroundColor: "oklch(0.62 0.18 35 / 0.08)",
                        border: "1px solid oklch(0.62 0.18 35 / 0.15)",
                      }}
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Mobile toggle */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            className="md:hidden p-2.5 rounded-xl transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{
              color: showBg ? "var(--foreground)" : "oklch(0.98 0.01 70)",
              backgroundColor: mobileOpen
                ? "oklch(0.62 0.18 35 / 0.1)"
                : "transparent",
            }}
          >
            <AnimatePresence mode="wait">
              {mobileOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X className="w-5 h-5" />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Menu className="w-5 h-5" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>
      </motion.nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="fixed top-16 left-4 right-4 z-40 md:hidden rounded-2xl overflow-hidden"
            style={{
              backgroundColor: "oklch(0.97 0.015 70 / 0.96)",
              backdropFilter: "blur(24px) saturate(1.4)",
              WebkitBackdropFilter: "blur(24px) saturate(1.4)",
              boxShadow: "0 16px 48px oklch(0.55 0.08 55 / 0.12), 0 4px 12px oklch(0.55 0.08 55 / 0.08)",
              border: "1px solid oklch(0.90 0.025 65 / 0.5)",
            }}
          >
            <div className="flex flex-col" style={{ padding: "var(--spacing-sm)" }}>
              {navItems.map((item, index) => {
                const Icon = item.icon;
                const active = location.pathname === item.path;
                return (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center rounded-xl transition-all duration-200"
                      style={{
                        gap: "var(--spacing-sm)",
                        padding: "var(--spacing-sm) var(--spacing-md)",
                        color: active ? "oklch(0.62 0.18 35)" : "var(--foreground)",
                        fontWeight: active ? 600 : 500,
                        backgroundColor: active
                          ? "oklch(0.62 0.18 35 / 0.08)"
                          : "transparent",
                      }}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                      {active && (
                        <motion.div
                          layoutId="mobile-active"
                          className="ml-auto w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: "oklch(0.62 0.18 35)" }}
                        />
                      )}
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
